import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../App'
import API from '../../services/API'
import moduleList from './moduleList'
import StepNav from './StepNav'
import TabSelector from './TabSelector'
import ActionButtons from './ActionButtons'
import SubmissionTracker from './SubmissionTracker'
import submitRequirementChecker from './submitRequirementChecker'
import Loading from '../loading/loadingPage'
import DemoSwitchButton from '../../components/demo/DemoSwitchButton'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ListIcon from '@mui/icons-material/FormatListNumbered'

export const ApplicationContext = createContext({})

// Steps that can show a complete/outstanding state (Review is the summary)
const CHECKED_STEPS = moduleList.filter((step) => step.key !== 'review')

const MyApplication = () => {
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSideBarOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { user, setUser } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [selectedModule, setSelectedModule] = useState(moduleList[0].key)
  const [formValues, setFormValues] = useState({
    prefix: user.prefix ?? '',
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    photograph: user.photograph,
    employee: {
      personnelInfo: {
        dob: null,
      },
    },
  })

  const loadApplication = () =>
    API.get('/my-employee-application')
      .then((res) => {
        if (res.status === 200) {
          setFormValues((prev) => ({
            ...prev,
            ...res.data.employeeApplication,
          }))
          setLoading(false)
        }
      })
      .catch((e) => console.log(e))

  useEffect(() => {
    loadApplication()
  }, [])

  const requiredFields = useMemo(
    () => submitRequirementChecker(formValues),
    [formValues],
  )
  const outstandingByStep = useMemo(
    () =>
      requiredFields.reduce((acc, field) => {
        acc[field.step] = (acc[field.step] ?? 0) + 1
        return acc
      }, {}),
    [requiredFields],
  )
  const completeCount = CHECKED_STEPS.filter(
    (step) => !outstandingByStep[step.key],
  ).length

  // The photograph is saved by its own upload flow and firstName is fixed at
  // registration, so neither rides along with a progress save.
  const saveProgress = () => {
    const { photograph, firstName, ...payload } = formValues
    setSaving(true)
    return API.post('/update-employee-own-application', payload)
      .then((res) => {
        if (res.status === 200) {
          setLastSaved(new Date())
          return true
        }
        setToast({
          message: 'Unable to save your application. Please try again.',
          severity: 'error',
        })
        return false
      })
      .catch(() => false)
      .finally(() => setSaving(false))
  }

  const goToStep = (key, { save = true } = {}) => {
    if (save && key !== selectedModule) saveProgress()
    setSelectedModule(key)
    setSideBarOpen(false)
    window.scrollTo(0, 0)
  }

  const handleSignOut = async () => {
    await saveProgress()
    API.delete('/logout')
      .then(() => setUser({}))
      .catch((e) => console.log(e))
  }

  const handleSubmit = () => {
    API.post('/update-employee-own-application', {
      ...formValues,
      state: 'Submitted',
      newSubmission: true,
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Application submitted' })
        setFormValues((prev) => ({ ...prev, state: 'Submitted' }))
        window.scrollTo(0, 0)
      } else {
        setToast({
          message: 'Unable to submit your application. Please try again.',
          severity: 'error',
        })
      }
    })
  }

  if (loading) return <Loading />

  const stepIndex = moduleList.findIndex((step) => step.key === selectedModule)
  const step = moduleList[stepIndex]
  const progress = Math.round((completeCount / CHECKED_STEPS.length) * 100)

  const nav = (
    <StepNav
      steps={moduleList}
      selected={selectedModule}
      outstandingByStep={outstandingByStep}
      completeCount={completeCount}
      totalCount={CHECKED_STEPS.length}
      onSelect={goToStep}
    />
  )

  return (
    <ApplicationContext.Provider
      value={{
        formValues,
        setFormValues,
        selectedModule,
        setSelectedModule,
        goToStep,
        saveProgress,
        saving,
        lastSaved,
        requiredFields,
        outstandingByStep,
        handleSubmit,
        handleSignOut,
      }}
    >
      {formValues.state === 'Draft' ? (
        <Box minHeight="100vh" bgcolor="anBackground.main">
          {/* HEADER */}
          <Box
            component="header"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: (theme) => theme.zIndex.appBar,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              px={{ xs: 2, md: 3 }}
              py={1}
              maxWidth={1280}
              mx="auto"
            >
              <Stack direction="row" alignItems="center" gap={1.5} minWidth={0}>
                {orgSettings?.logo && (
                  <Box
                    component="img"
                    src={orgSettings.logo}
                    alt=""
                    sx={{ height: 40, maxWidth: 120, objectFit: 'contain' }}
                  />
                )}
                <Box minWidth={0}>
                  <Typography fontWeight={600} noWrap>
                    Job application
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {saving
                      ? 'Saving…'
                      : lastSaved
                        ? `Saved at ${moment(lastSaved).format('HH:mm')}`
                        : 'Your progress saves as you move between steps'}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1.5}>
                {/* Renders null unless this is a demo tenant */}
                <DemoSwitchButton />
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  display={{ xs: 'none', sm: 'flex' }}
                >
                  <Avatar
                    sx={{ width: 28, height: 28 }}
                    src={user.photograph}
                  />
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {user.firstName} {user.lastName}
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={handleSignOut}
                  disabled={saving}
                >
                  Save & sign out
                </Button>
              </Stack>
            </Stack>
            {/* Mobile step summary — the full list opens in a drawer */}
            <Box display={{ xs: 'block', md: 'none' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                px={2}
                pb={1}
              >
                <Typography variant="body2" noWrap>
                  Step {stepIndex + 1} of {moduleList.length} ·{' '}
                  <Box component="span" fontWeight={600} color="text.primary">
                    {step.title}
                  </Box>
                </Typography>
                <Button
                  size="small"
                  startIcon={<ListIcon />}
                  onClick={() => setSideBarOpen(true)}
                >
                  All steps
                </Button>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 3 }}
              />
            </Box>
          </Box>

          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSideBarOpen(false)}
            PaperProps={{ sx: { width: 300, p: 2 } }}
          >
            {nav}
          </Drawer>

          <Stack
            direction="row"
            gap={3}
            px={{ xs: 1.5, sm: 2, md: 3 }}
            py={{ xs: 2, md: 3 }}
            maxWidth={1280}
            mx="auto"
            alignItems="flex-start"
          >
            <Paper
              sx={{
                display: { xs: 'none', md: 'block' },
                width: 280,
                flexShrink: 0,
                p: 2,
                position: 'sticky',
                top: 80,
              }}
            >
              {nav}
            </Paper>

            <Box flex={1} minWidth={0}>
              <Box mb={2} px={{ xs: 0.5, md: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Step {stepIndex + 1} of {moduleList.length}
                </Typography>
                <Typography variant="h5" component="h1">
                  {step.title}
                </Typography>
                <Typography variant="body2" mt={0.5}>
                  {step.description}
                </Typography>
              </Box>
              <TabSelector
                selectedTab={selectedModule}
                formValues={formValues}
                setFormValues={setFormValues}
              />
              <ActionButtons />
            </Box>
          </Stack>
        </Box>
      ) : (
        <>
          {/* No header in the tracker view — float the demo switcher */}
          <DemoSwitchButton floating />
          <SubmissionTracker application={formValues} refresh={loadApplication} />
        </>
      )}
    </ApplicationContext.Provider>
  )
}

export default MyApplication
